import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "../App.css";
import RightPanel from "./RightPanel";
import Sidebar from "./Sidebar";
import ChatHeader from "./ChatHeader";
import ChatActions from "./ChatActions";
import MessageList from "./MessageList";
import ChatInput from "./ChatInput";
import { useSessions } from "../hooks/useSessions";
import { useSessionHistory } from "../hooks/useSessionHistory";
import { useChat } from "../hooks/useChat";

function MainApp() {
  const navigate = useNavigate();
  const backendUrl =
    process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";

  // Right panel state
  const [showRightPanel, setShowRightPanel] = useState(false);
  const [rightPanelTab, setRightPanelTab] = useState("prd");

  // Per-session PRD content storage
  const [sessionPrdContent, setSessionPrdContent] = useState({});

  // Session management hook
  const {
    sessionIds,
    sessionTitles,
    sessionUserEmails,
    currentSessionId,
    setCurrentSessionId,
    isStartingSession,
    handleStartChatSession,
    updateSessionTitle,
  } = useSessions(backendUrl);

  // Get current user email to check if admin
  const currentUserEmail = localStorage.getItem("userEmail");
  const isAdmin = currentUserEmail === "admin@vamoos.com";

  // Helper function to set PRD content for a specific session
  const setCurrentPrdContent = (prdContent, sessionId = null) => {
    const targetSessionId = sessionId || currentSessionId;
    if (targetSessionId) {
      setSessionPrdContent((prev) => ({
        ...prev,
        [targetSessionId]: prdContent,
      }));
    }
  };

  // Get current session's PRD content
  const currentPrdContent = currentSessionId
    ? sessionPrdContent[currentSessionId] || ""
    : "";

  // Chat management hook
  const {
    messages,
    setMessages,
    messageInput,
    setMessageInput,
    isSending,
    handleSendMessage,
    handleKeyPress,
  } = useChat(
    backendUrl,
    currentSessionId,
    setCurrentPrdContent,
    updateSessionTitle
  );

  // Session history hook - loads history when session changes
  useSessionHistory(
    backendUrl,
    currentSessionId,
    setMessages,
    setCurrentPrdContent,
    updateSessionTitle
  );

  // Handler functions for UI actions
  const handleLoadEWA = () => {
    if (!currentSessionId) {
      alert("Please start a chat session first!");
      return;
    }
    const ewaUrl = `${backendUrl}/sessions/${currentSessionId}/ewa.zip`;
    window.open(ewaUrl, "_blank");
  };

  const handleOpenPRD = () => {
    if (!currentSessionId) {
      alert("Please start a chat session first!");
      return;
    }
    setRightPanelTab("prd");
    setShowRightPanel(true);
  };

  const handleOpenPreview = () => {
    if (!currentSessionId) {
      alert("Please start a chat session first!");
      return;
    }
    setRightPanelTab("preview");
    setShowRightPanel(true);
  };

  const handleCloseRightPanel = () => {
    setShowRightPanel(false);
  };

  const handleLogout = () => {
    localStorage.removeItem("isAuthenticated");
    localStorage.removeItem("userId");
    localStorage.removeItem("userEmail");
    navigate("/login");
  };

  return (
    <div className="App">
      <Sidebar
        sessionIds={sessionIds}
        sessionTitles={sessionTitles}
        sessionUserEmails={sessionUserEmails}
        isAdmin={isAdmin}
        currentSessionId={currentSessionId}
        onSessionSelect={setCurrentSessionId}
        onNewChat={handleStartChatSession}
        isStartingSession={isStartingSession}
      />

      <main className="main-content">
        <ChatHeader onLogout={handleLogout} />

        <div className="content-area">
          <div className="chat-section">
            <h3>Vamoos Studio chat</h3>

            <div className="chat-container">
              <ChatActions
                currentSessionId={currentSessionId}
                isStartingSession={isStartingSession}
                onStartSession={handleStartChatSession}
                onOpenPRD={handleOpenPRD}
                onOpenPreview={handleOpenPreview}
                onDownloadEWA={handleLoadEWA}
              />

              <MessageList messages={messages} isSending={isSending} />

              <ChatInput
                messageInput={messageInput}
                onMessageChange={setMessageInput}
                onSend={handleSendMessage}
                onKeyPress={handleKeyPress}
                isSending={isSending}
                currentSessionId={currentSessionId}
              />
            </div>
          </div>
        </div>
      </main>

      {showRightPanel && (
        <RightPanel
          sessionId={currentSessionId}
          isVisible={showRightPanel}
          onClose={handleCloseRightPanel}
          prdContent={currentPrdContent}
          onPrdUpdate={setCurrentPrdContent}
          initialTab={rightPanelTab}
          isSending={isSending}
        />
      )}
    </div>
  );
}

export default MainApp;
